import { sanitizeUrl } from '@repo/common/utils/urls';
import { InputRule } from 'prosemirror-inputrules';
import type { ParseSpec } from 'prosemirror-markdown';
import type {
	Attrs,
	NodeSpec,
	Node as ProsemirrorNode,
} from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import { NodeSelection, Plugin, TextSelection } from 'prosemirror-state';
import insertFiles from '../commands/insertFiles';
import Caption from '../components/ImageContainer/Caption';
import { ImageContainerV2 } from '../components/ImageContainer/ImageContainerV2';
import dictionary from '../dictionary';
import type { MarkdownSerializerState } from '../lib/markdown/serializer';
import uploadPlaceholderPlugin from '../lib/uploadPlaceholder';
import { uploadPlugin } from '../lib/uploadPlugin';
import type { Dispatch, SecodaEditorComponentProps } from '../types';
import { getEventFiles } from '../utils/files';
import { extractImageSizesFromSrc } from '../utils/image';
import type { NodeOptions } from './Node';
import ReactNode from './ReactNode';

/**
 * Matches following attributes in Markdown-typed image: [, caption, src, class]
 *
 * Example:
 * ![Lorem](image.jpg) -> [, "Lorem", "image.jpg"]
 * ![](image.jpg "class") -> [, "", "image.jpg", "small"]
 * ![Lorem](image.jpg "class") -> [, "Lorem", "image.jpg", "small"]
 */
const IMAGE_INPUT_REGEX =
	/!\[(?<caption>[^\]\[]*?)]\((?<filename>[^\]\[]*?)(?=\“|\))\“?(?<layoutclass>[^\]\[\"]+)?\”?\)$/;

const IMAGE_CLASSES = ['right-50', 'left-50', 'full-width'];

const getLayoutAndTitle = (tokenTitle: string) => {
	if (!tokenTitle) return {};
	if (IMAGE_CLASSES.includes(tokenTitle)) {
		return {
			layoutClass: tokenTitle,
		};
	}
	return {
		title: tokenTitle,
	};
};

const downloadImageNode = async (node: ProsemirrorNode) => {
	const image = await fetch(node.attrs.src);
	const imageBlob = await image.blob();
	const imageURL = URL.createObjectURL(imageBlob);
	const extension = imageBlob.type.split('/')[1];
	const potentialName = node.attrs.caption || 'image';
	// Create a temporary link node and click it with our image data
	const link = document.createElement('a');
	link.href = imageURL;
	link.download = `${potentialName}.${extension}`;
	document.body.appendChild(link);
	link.click();
	// Cleanup
	document.body.removeChild(link);
};

export default class Image extends ReactNode {
	get name() {
		return 'image';
	}

	get schema(): NodeSpec {
		return {
			inline: true,
			attrs: {
				src: {},
				prevSrc: {
					default: null,
				},
				caption: {
					default: null,
				},
				prevCaption: {
					default: null,
				},
				title: {
					default: null,
				},
				prevTitle: {
					default: null,
				},
				layoutClass: {
					default: null,
				},

				width: {
					default: null,
				},
				height: {
					default: null,
				},
				isRemoved: {
					default: false,
				},
			},
			content: 'text*',
			marks: '',
			group: 'inline',
			selectable: true,
			draggable: false,
			atom: true,
			parseDOM: [
				{
					tag: 'div[class~=image]',
					getAttrs: (dom: HTMLElement | string) => {
						if (typeof dom === 'string') {
							return null;
						}

						const img = dom.getElementsByTagName('img')[0];
						const { className } = dom;
						const layoutClassMatched =
							className && className.match(/image-(.*)$/);
						const layoutClass = layoutClassMatched
							? layoutClassMatched[1]
							: null;
						return {
							src: img?.getAttribute('src'),
							prevSrc: img?.getAttribute('data-diff-image-prev-src'),
							caption: img?.getAttribute('caption'),
							prevCaption: img?.getAttribute('data-diff-image-prev-caption'),
							title: img?.getAttribute('title'),
							prevTitle: img?.getAttribute('data-diff-image-prev-title'),
							isRemoved: img?.getAttribute('data-diff-image-is-removed'),
							width: img?.getAttribute('width'),
							height: img?.getAttribute('height'),
							layoutClass,
						};
					},
				},
				{
					tag: 'img',
					getAttrs: (dom: HTMLElement | string) =>
						typeof dom === 'string'
							? null
							: {
									src: dom.getAttribute('src'),
									caption: dom.getAttribute('caption'),
									title: dom.getAttribute('title'),
									width: dom.getAttribute('width'),
									height: dom.getAttribute('height'),
									prevSrc: dom.getAttribute('data-diff-image-prev-src'),
									prevCaption: dom.getAttribute('data-diff-image-prev-caption'),
									prevTitle: dom.getAttribute('data-diff-image-prev-title'),
									isRemoved: dom.getAttribute('data-diff-image-is-removed'),
								},
				},
			],
			toDOM: (node) => {
				const className = node.attrs.layoutClass
					? `image image-${node.attrs.layoutClass}`
					: 'image';

				const children = [
					[
						'img',
						{
							...node.attrs,
							src: sanitizeUrl(node.attrs.src),
							width: node.attrs.width,
							height: node.attrs.height,
							contentEditable: 'false',
						},
					],
				];

				if (node.attrs.caption) {
					children.push(['p', { class: 'caption' }, node.attrs.caption]);
				}

				return [
					'div',
					{
						class: className,
					},
					...children,
				];
			},
		};
	}

	handleKeyDown =
		({ node, getPos }: SecodaEditorComponentProps) =>
		(event: React.KeyboardEvent<HTMLParagraphElement>) => {
			// Pressing Enter in the caption field should move the cursor/selection
			// below the image
			if (event.key === 'Enter') {
				event.preventDefault();
				const { view } = this.editorState;
				const $pos = view.state.doc.resolve(getPos() + node.nodeSize);
				view.dispatch(
					view.state.tr.setSelection(new TextSelection($pos)).split($pos.pos)
				);
				view.focus();
				return;
			}
			// Pressing Backspace in an an empty caption field should remove the entire
			// image, leaving an empty paragraph
			if (event.key === 'Backspace' && event.currentTarget.innerText === '') {
				const { view } = this.editorState;
				const $pos = view.state.doc.resolve(getPos());
				const tr = view.state.tr.setSelection(new NodeSelection($pos));
				view.dispatch(tr.deleteSelection());
				view.focus();
			}
		};

	handleBlur =
		({ node, getPos }: SecodaEditorComponentProps) =>
		(event: React.FocusEvent<HTMLParagraphElement>) => {
			const caption = event.target.innerText;
			const { src, title, layoutClass } = node.attrs;
			if (caption === node.attrs.caption) return;
			const { view } = this.editorState;
			const { tr } = view.state;
			// Update meta on object
			const pos = getPos();
			const transaction = tr.setNodeMarkup(pos, undefined, {
				src,
				caption,
				title,
				layoutClass,
			});
			view.dispatch(transaction);
		};

	handleSelect =
		({ getPos }: SecodaEditorComponentProps) =>
		(event: React.MouseEvent) => {
			event.preventDefault();
			const { view } = this.editorState;
			const $pos = view.state.doc.resolve(getPos());
			const transaction = view.state.tr.setSelection(new NodeSelection($pos));
			view.dispatch(transaction);
		};

	handleResize = ({
		node,
		size,
	}: {
		node: ProsemirrorNode;
		size: {
			width: number | string;
			height: number | string;
		};
	}) => {
		const {
			view: { dispatch, state },
		} = this.editorState;
		const nodeAttrs = node?.attrs || {};
		const attrs = {
			...((state.selection as NodeSelection)?.node?.attrs ?? {}),
			...nodeAttrs,
			title: null,
			width: size.width,
		};
		const { selection } = state;
		dispatch(state.tr.setNodeMarkup(selection.from, undefined, attrs));
		return true;
	};

	handleChangeWidth =
		({ node, getPos }: { node: ProsemirrorNode; getPos: () => number }) =>
		(width: number) => {
			const { commands } = this.editor;
			const {
				view: { dispatch, state },
			} = this.editorState;

			const { doc, tr } = state;

			const pos = getPos();
			const $pos = doc.resolve(pos);

			dispatch(tr.setSelection(new NodeSelection($pos)));
			commands['resizeImage']({ width });
		};

	// handleDownload =
	// 	({ node }: { node: ProsemirrorNode }) =>
	// 		(event: React.MouseEvent) => {
	// 			event.preventDefault();
	// 			event.stopPropagation();
	// 			void downloadImageNode(node);
	// 		};

	handleCaptionKeyDown =
		({ node, getPos }: { node: ProsemirrorNode; getPos: () => number }) =>
		(event: React.KeyboardEvent<HTMLParagraphElement>) => {
			const { view } = this.editorState;

			// Pressing Enter in the caption field should move the cursor/selection
			// below the image and create a new paragraph.
			if (event.key === 'Enter') {
				event.preventDefault();

				const $pos = view.state.doc.resolve(getPos() + node.nodeSize);
				view.dispatch(
					view.state.tr
						.setSelection(TextSelection.near($pos))
						.split($pos.pos)
						.scrollIntoView()
				);
				view.focus();
				return;
			}

			// Pressing Backspace in an an empty caption field focused the image.
			if (event.key === 'Backspace' && event.currentTarget.innerText === '') {
				event.preventDefault();
				event.stopPropagation();
				const $pos = view.state.doc.resolve(getPos());
				const tr = view.state.tr.setSelection(new NodeSelection($pos));
				view.dispatch(tr);
				view.focus();
				return;
			}
		};

	handleCaptionBlur =
		({ node, getPos }: { node: ProsemirrorNode; getPos: () => number }) =>
		(event: React.FocusEvent<HTMLParagraphElement>) => {
			const caption = event.currentTarget.innerText;
			if (caption === node.attrs.caption) {
				return;
			}

			const { view } = this.editorState;
			const { tr } = view.state;

			// update meta on object
			const pos = getPos();
			const transaction = tr.setNodeMarkup(pos, undefined, {
				...node.attrs,
				caption: caption,
			});
			view.dispatch(transaction);
		};

	component = (props: SecodaEditorComponentProps) => (
		<ImageContainerV2
			{...props}
			onClick={this.handleSelect(props)}
			// onDownload={this.handleDownload(props)}
			onChangeWidth={this.handleChangeWidth(props)}
		>
			<Caption
				width={props.node.attrs.width}
				onBlur={this.handleCaptionBlur(props)}
				onKeyDown={this.handleCaptionKeyDown(props)}
				isSelected={props.isSelected}
				placeholder={dictionary.imageCaptionPlaceholder}
				caption={props.node.attrs.caption}
			/>
		</ImageContainerV2>
	);

	toMarkdown(state: MarkdownSerializerState, node: ProsemirrorNode) {
		let markdown = `![${state.esc(
			(node.attrs.caption || '').replace('\n', '') || '',
			false
		)}](${state.esc(node.attrs.src, false)}`;
		if (node.attrs.width) {
			// we don't serialize height to avoid distorting images
			markdown += `=${node.attrs.width > 25 ? node.attrs.width : 250}`;
		}
		if (node.attrs.layoutClass) {
			markdown += ` "${state.esc(node.attrs.layoutClass)}"`;
		} else if (node.attrs.title) {
			markdown += ` "${state.esc(node.attrs.title)}"`;
		}
		markdown += ')';
		// Do not add a newline – this will break embedding images in tables, since
		// the newline will be parsed as a new cell, and a double escape is
		// required, `\\\n`.
		state.write(markdown);
		if (!state.inTable) {
			// we can't have the standard \n\n at the end of the image if inside a table
			state.closeBlock(node);
		}
	}

	parseMarkdown(): ParseSpec {
		return {
			node: 'image',
			getAttrs: (token) => {
				// Initial Implementation
				let src = token.attrGet('src') ?? '';
				let caption =
					(token?.children && token.children[0] && token.children[0].content) ||
					null;

				const { width } = extractImageSizesFromSrc(src);
				src = src.replace(/=[^=]*$/, ''); // should only match the last '=width' in the URL

				let previousSrc = token.attrGet('data-diff-image-prev-src');
				previousSrc =
					previousSrc !== undefined && previousSrc !== null
						? previousSrc.replace(/=[^=]*$/, '')
						: null;
				const previousCaption = token.attrGet('data-diff-image-prev-caption');
				const previousTitle = token.attrGet('data-diff-image-prev-title');

				const isRemoved = token.attrGet('data-diff-image-is-removed');

				return {
					src,
					prevSrc: previousSrc,
					caption,
					prevCaption: previousCaption,
					title: token.attrGet('title'),
					prevTitle: previousTitle,
					isRemoved: isRemoved === 'true',
					width,
					// we don't set height to avoid distorting images
					height: null,
					...getLayoutAndTitle(token.attrGet('title') ?? ''),
				};
			},
		};
	}

	commands({ type }: NodeOptions) {
		return {
			downloadImage: () => (state: EditorState) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const { node } = state.selection;
				if (node.type.name !== 'image') {
					return false;
				}
				downloadImageNode(node);
				return true;
			},
			deleteImage: () => (state: EditorState, dispatch: Dispatch) => {
				dispatch(state.tr.deleteSelection());
				return true;
			},
			alignRight: () => (state: EditorState, dispatch: Dispatch) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const attrs = {
					...(state.selection.node?.attrs ?? {}),
					title: null,
					layoutClass: 'right-50',
				};
				const { selection } = state;
				dispatch(state.tr.setNodeMarkup(selection.from, undefined, attrs));
				return true;
			},
			alignLeft: () => (state: EditorState, dispatch: Dispatch) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const attrs = {
					...(state.selection.node?.attrs ?? {}),
					title: null,
					layoutClass: 'left-50',
				};
				const { selection } = state;
				dispatch(state.tr.setNodeMarkup(selection.from, undefined, attrs));
				return true;
			},
			alignFullWidth: () => (state: EditorState, dispatch: Dispatch) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const attrs = {
					...(state.selection.node?.attrs ?? {}),
					title: null,
					layoutClass: 'full-width',
				};
				const { selection } = state;
				dispatch(state.tr.setNodeMarkup(selection.from, undefined, attrs));
				return true;
			},
			replaceImage: () => (state: EditorState) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const { view } = this.editorState;
				const { node } = state.selection;
				const { uploadFile, onFileUploadStart, onFileUploadStop, onShowToast } =
					this.editorState;
				if (!uploadFile) {
					throw new Error('uploadFile prop is required to replace images');
				}
				if (node.type.name !== 'image') {
					return false;
				}
				// Create an input element and click to trigger picker
				const inputElement = document.createElement('input');
				inputElement.type = 'file';
				inputElement.accept = 'image/*';
				inputElement.onchange = (event: Event) => {
					const files = getEventFiles(event);
					insertFiles(view, event, state.selection.from, files, {
						uploadFile,
						onFileUploadStart,
						onFileUploadStop,
						onShowToast,
						dictionary,
						replaceExisting: true,
					});
				};
				inputElement.click();
				return true;
			},
			alignCenter: () => (state: EditorState, dispatch: Dispatch) => {
				if (!(state.selection instanceof NodeSelection)) {
					return false;
				}
				const attrs = {
					...(state.selection.node?.attrs ?? {}),
					layoutClass: null,
				};
				const { selection } = state;
				dispatch(state.tr.setNodeMarkup(selection.from, undefined, attrs));
				return true;
			},
			createImage:
				(attrs?: Attrs) => (state: EditorState, dispatch: Dispatch) => {
					const { selection } = state;
					const position =
						selection instanceof TextSelection
							? selection.$cursor?.pos
							: selection.$to.pos;
					if (position === undefined) {
						return false;
					}
					const node = type.create(attrs);
					const transaction = state.tr.insert(position, node);
					dispatch(transaction);
					return true;
				},
			resizeImage:
				(attrs?: Attrs) => (state: EditorState, dispatch: Dispatch) => {
					if (!(state.selection instanceof NodeSelection)) {
						return false;
					}

					const { selection } = state;
					const transformedAttrs = {
						...state.selection.node.attrs,
						...(attrs ?? {}),
					};

					const tr = state.tr
						.setNodeMarkup(selection.from, undefined, transformedAttrs)
						.setMeta('addToHistory', true);

					const $pos = tr.doc.resolve(selection.from);
					dispatch?.(tr.setSelection(new NodeSelection($pos)));
					return true;
				},
		};
	}

	keys({ type, schema }: NodeOptions) {
		return {
			Enter: (state: EditorState, dispatch?: Dispatch) => {
				const { selection } = state;
				if (
					selection instanceof NodeSelection &&
					selection.node?.type.name === type.name
				) {
					const { tr } = state;
					dispatch?.(
						tr
							.insert(selection.to, schema.nodes.paragraph.create({}))
							.setSelection(
								TextSelection.near(tr.doc.resolve(selection.to + 2), 1)
							)
							.scrollIntoView()
					);
					return true;
				}

				return false;
			},
		};
	}

	inputRules({ type }: NodeOptions) {
		return [
			new InputRule(IMAGE_INPUT_REGEX, (state, match, start, end) => {
				const [okay, caption, src, matchedTitle] = match;
				const { tr } = state;
				if (okay) {
					tr.replaceWith(
						start - 1,
						end,
						type.create({
							src,
							caption,
							...getLayoutAndTitle(matchedTitle),
						})
					);
				}
				return tr;
			}),
		];
	}

	get plugins() {
		return [
			uploadPlaceholderPlugin,
			uploadPlugin(this.options),
			new Plugin({
				props: {
					handleKeyDown: (view, event) => {
						// prevent prosemirror's default spacebar behavior
						// & zoom in if the selected node is image
						if (event.key === ' ') {
							const { state } = view;
							const { selection } = state;
							if (selection instanceof NodeSelection) {
								const { node } = selection;
								if (node.type.name === 'image') {
									const image = document.querySelector(
										'.ProseMirror-selectednode img'
									) as HTMLImageElement;
									image.click();
									return true;
								}
							}
						}

						return false;
					},
				},
			}),
		];
	}
}
